/**
 * Methodology.tsx
 *
 * W66.C — Methodology Workspace
 * W66.X17 — Added H5 Doctrine horizon: methodology-as-engine doctrine,
 *            M-PACT pillars, three-horizon summary, Reference Library links.
 * Replaces prior /methodology content (W54 Path A/B comparison page).
 * Prior content backed up to:
 *   /home/user/workspace/_backup_4track_2026-05-27/w66-c/methodology-old.tsx
 *
 * Five-horizon layout:
 *   H1 — CP Catalog         (10 M-PACT checkpoints, weights, descriptions)
 *   H2 — Process Flow       (flowchart preview + link to /flowcharts)
 *   H3 — Tower View         (pipeline tower preview + link to /pipeline-tower, /tower)
 *   H4 — Helix View         (DNA health model preview + link to /helix)
 *   H5 — Doctrine           (methodology-as-engine doctrine + Reference Library links)
 *
 * Original routes remain live as deep links:
 *   /flowcharts, /pipeline-tower, /tower, /helix, /opportunity-dna
 *
 * Component reuse policy:
 *   - FlowchartWall: NOT embedded inline. Requires live Supabase question data
 *     and a full-height canvas context. Thumbnail + CTA used (HARD STOP rule).
 *   - PipelineTower / Tower: NOT embedded inline. Require live Supabase deal data
 *     and account selection. Thumbnail + CTA used (HARD STOP rule).
 *   - OperationalHelix: NOT embedded inline. Requires mandatory accountId prop
 *     and account-scoped localStorage answers. Thumbnail + CTA used (HARD STOP rule).
 *
 * CANON (NON-NEGOTIABLE):
 *   No exclamation points. No emojis. M-PACT hyphenated.
 *   Em-dash format CP{N} — {Name}. "Checkpoint" not "Gate".
 *   Banned: BANT, MEDDIC, HARD-KILL, HARD-STOP, SOFT-STOP, REFRESH,
 *           "People Mapping", "Fit" as CP, "Compelling event", "Amber".
 *   Bryan K. Short, Founder and CEO, Mi6 Group. Tenant 1 only.
 *
 * Approved: Bryan K. Short, 2026-05-31 10:02 PM EDT.
 */

import OperatorShell from "@/components/operator/OperatorShell";
import HorizonHeader from "@/components/methodology/HorizonHeader";
import CPCatalogHorizon from "@/components/methodology/CPCatalogHorizon";
import ProcessFlowHorizon from "@/components/methodology/ProcessFlowHorizon";
import TowerHorizon from "@/components/methodology/TowerHorizon";
import HelixHorizon from "@/components/methodology/HelixHorizon";
import DoctrineHorizon from "@/components/methodology/DoctrineHorizon";

// ─── Divider between horizon sections ────────────────────────────────────────

function HorizonDivider() {
  return (
    <hr
      style={{
        border: "none",
        borderTop: "1px solid #E5E5E7",
        margin: "40px 0",
      }}
    />
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Methodology() {
  return (
    <OperatorShell
      breadcrumbs={[{ label: "Home", href: "/" }, { label: "Methodology" }]}
      pageTitle="Methodology Workspace"
    >
      {/* ── Page header ────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 40, maxWidth: 680 }}>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 800,
            color: "#0B0B0F",
            letterSpacing: "-0.02em",
            margin: "0 0 8px",
          }}
        >
          Methodology Workspace
        </h1>
        <p style={{ fontSize: 14, color: "#6B6B70", lineHeight: 1.65, margin: 0 }}>
          The M-PACT framework across four horizons — doctrine, process, architecture, and
          health diagnostics — in one navigable surface. Each horizon section links to the
          full deep-link view for detailed work.
        </p>

        {/* Quick-nav strip */}
        <div
          style={{
            marginTop: 20,
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {[
            { label: "H1 — CP Catalog", id: "h1-cp-catalog" },
            { label: "H2 — Process Flow", id: "h2-process-flow" },
            { label: "H3 — Tower View", id: "h3-tower-view" },
            { label: "H4 — Helix View", id: "h4-helix-view" },
            { label: "H5 — Doctrine", id: "h5-doctrine" },
          ].map(({ label, id }) => (
            <button
              key={id}
              type="button"
              onClick={() =>
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#0B0B0F",
                background: "#FAFAF7",
                border: "1px solid #E5E5E7",
                borderRadius: 6,
                padding: "5px 12px",
                cursor: "pointer",
                transition: "background 0.15s",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── H1 — CP Catalog ────────────────────────────────────────────────── */}
      <div id="h1-cp-catalog">
        <HorizonHeader
          number={1}
          label="CP Catalog"
          description="The ten M-PACT checkpoints — weights, disqualifier status, doctrine notes, and key questions. These definitions are the source of record for every CP reference in the platform."
        />
        <CPCatalogHorizon />
      </div>

      <HorizonDivider />

      {/* ── H2 — Process Flow ──────────────────────────────────────────────── */}
      <div id="h2-process-flow">
        <HorizonHeader
          number={2}
          label="Process Flow"
          description="The M-PACT question-decision tree for each checkpoint. Shows how answers branch, how evidence requirements activate, and how the flow differs between New Customer and Existing Fast Lane engagements."
        />
        <ProcessFlowHorizon />
      </div>

      <HorizonDivider />

      {/* ── H3 — Tower View ────────────────────────────────────────────────── */}
      <div id="h3-tower-view">
        <HorizonHeader
          number={3}
          label="Tower View"
          description="The architectural visualization of the pipeline. Each deal occupies a floor (checkpoint level) in the tower. The aggregate view shows the health of the entire book of business at a glance."
        />
        <TowerHorizon />
      </div>

      <HorizonDivider />

      {/* ── H4 — Helix View ────────────────────────────────────────────────── */}
      <div id="h4-helix-view">
        <HorizonHeader
          number={4}
          label="Helix View"
          description="The DNA health diagnostic. Each checkpoint maps to a health orb on the double-helix spine. Orb color reflects answer strength from Strong through Early. Account-scoped — open from any account to see live health data."
        />
        <HelixHorizon />
      </div>

      <HorizonDivider />

      {/* ── H5 — Doctrine ──────────────────────────────────────────────── */}
      <div id="h5-doctrine">
        <HorizonHeader
          number={5}
          label="Doctrine"
          description="The methodology-as-engine doctrine — how Sales Plan, M-PACT, and the operating surface work as one. M-PACT pillars, three-horizon model, and links to the full Reference Library."
        />
        <DoctrineHorizon />
      </div>

      {/* ── Footer note ────────────────────────────────────────────────────── */}
      <div
        style={{
          marginTop: 48,
          paddingTop: 24,
          borderTop: "1px solid #E5E5E7",
          fontSize: 12,
          color: "#9CA3AF",
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: "#6B6B70" }}>M-PACT Methodology — Mi6 Group</strong>
        {" — "}
        Ten checkpoints, locked weights, question-driven qualification. Bryan K. Short,
        Founder and CEO, Mi6 Group. W66.C locked 2026-05-31.
        <br />
        Deep links remain live:{" "}
        <a href="/flowcharts" style={{ color: "#6B6B70" }}>/flowcharts</a>
        {" · "}
        <a href="/pipeline-tower" style={{ color: "#6B6B70" }}>/pipeline-tower</a>
        {" · "}
        <a href="/tower" style={{ color: "#6B6B70" }}>/tower</a>
        {" · "}
        <a href="/helix" style={{ color: "#6B6B70" }}>/helix</a>
      </div>
    </OperatorShell>
  );
}
